import React from 'react';
import { Input as InputBase } from '../Input';
import { Select as SelectBase } from '../Select';
import { Textarea as TextareaBase } from '../Textarea';
import { Checkbox as CheckboxBase } from '../Checkbox';
import { Field as FieldBase, Label as LabelBase } from '../Fieldset';
import { SchemaField, FormData, FormErrors } from './types';

// Cast untyped JS components to accept all props
const Input = InputBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'input'>
>;
const Select = SelectBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'select'> & { options?: unknown[] }
>;
const Textarea = TextareaBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'textarea'>
>;
const Checkbox = CheckboxBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'input'>
>;
const Field = FieldBase as React.ComponentType<{ children?: React.ReactNode }>;
const Label = LabelBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'label'>
>;

interface FormFieldProps {
  field: SchemaField;
  formData: FormData;
  errors: FormErrors;
  textareaMinHeight?: string;
  getDisplayValue: (field: SchemaField) => string;
  getInputClass: (fieldName: string, baseClass?: string) => string;
  handleChange: (name: string, value: string | boolean) => void;
  handleArrayChange: (name: string, idx: number, value: string) => void;
  addArrayItem: (name: string) => void;
  removeArrayItem: (name: string, idx: number) => void;
}

export function FormField({
  field,
  formData,
  errors,
  textareaMinHeight = 'min-h-[100px]',
  getDisplayValue,
  getInputClass,
  handleChange,
  handleArrayChange,
  addArrayItem,
  removeArrayItem,
}: FormFieldProps) {
  return (
    <Field>
      <Label
        htmlFor={field.name}
        className="block text-sm font-medium text-gray-700 mb-2 font-baijamjuree"
      >
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </Label>

      {field.type === 'array' ? (
        <div className="space-y-3">
          {(formData[field.name] as string[]).map((val, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <Input
                id={`${field.name}-${idx}`}
                value={val}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleArrayChange(field.name, idx, e.target.value)
                }
                className={getInputClass(field.name, 'flex-1')}
                placeholder={field.placeholder}
              />
              <button
                type="button"
                onClick={() => removeArrayItem(field.name, idx)}
                className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center transition-all text-sm"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayItem(field.name)}
            className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-3 py-2 transition-all"
          >
            + Add Item
          </button>
        </div>
      ) : field.type === 'select' ? (
        <Select
          id={field.name}
          value={formData[field.name] as string}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            handleChange(field.name, e.target.value)
          }
          options={field.options || []}
          className={getInputClass(field.name)}
          disabled={field.readonly}
        />
      ) : field.type === 'textarea' ? (
        <Textarea
          id={field.name}
          value={getDisplayValue(field)}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            handleChange(field.name, e.target.value)
          }
          className={getInputClass(field.name, `${textareaMinHeight} resize-y`)}
          readOnly={field.readonly}
          placeholder={field.placeholder}
        />
      ) : field.type === 'checkbox' ? (
        <div className="flex items-center space-x-3 mt-2">
          <Checkbox
            id={field.name}
            checked={formData[field.name] as boolean}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(field.name, e.target.checked)
            }
            className="h-4 w-4 text-gray-900 focus:ring-gray-500/50 border-gray-300 rounded"
          />
          <Label
            htmlFor={field.name}
            className="text-sm text-gray-700 font-baijamjuree"
          >
            {field.placeholder || field.label}
          </Label>
        </div>
      ) : (
        <Input
          id={field.name}
          value={getDisplayValue(field)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(field.name, e.target.value)
          }
          className={getInputClass(field.name)}
          readOnly={field.readonly}
          placeholder={field.placeholder}
        />
      )}

      {errors[field.name] && (
        <div className="mt-2 text-sm text-red-600 font-baijamjuree">
          {errors[field.name].join(', ')}
        </div>
      )}
    </Field>
  );
}
